import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase-config";
import { collection, getDocs, query, doc, getDoc, where, deleteDoc, addDoc, updateDoc, Timestamp, orderBy } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./AdminDashboard.css";
import { jsPDF } from "jspdf";

const autoTable = require('jspdf-autotable').default;

// Fonction pour formater les informations complètes de l'appareil
function formatDeviceInfo(device) {
  if (!device) return "Non précisé";
  
  // Si c'est une chaîne de caractères (User Agent complet)
  if (typeof device === 'string') {
    return device;
  }
  
  // Si c'est un objet avec un userAgent
  if (device.userAgent) {
    return device.userAgent;
  }
  
  // Sinon, formater depuis les propriétés de l'objet
  const parts = [];
  
  if (device.os) {
    parts.push(device.os);
  } else if (device.platform) {
    parts.push(device.platform);
  }
  
  if (device.browser) {
    parts.push(device.browser);
  }
  
  if (device.browserVersion) {
    parts.push(`v${device.browserVersion}`);
  }
  
  if (device.type) {
    parts.push(`(${device.type})`);
  }
  
  return parts.length > 0 ? parts.join(' ') : "Inconnu";
}

// Colonnes à utiliser pour l'affichage, le CSV et le PDF
const ADMIN_COLUMNS = [
  { key: 'name', label: 'Nom' },
  { key: 'date', label: 'Date' },
  { key: 'startTime', label: 'Heure Début' },
  { key: 'endTime', label: 'Heure Fin' },
  { key: 'workTime', label: 'Temps Travail' },
  { key: 'pauseTime', label: 'Temps Pause' },
  { key: 'sites', label: 'Sites autorisés' },
  { key: 'gpsStart', label: 'GPS Début' },
  { key: 'gpsEnd', label: 'GPS Fin' },
  { key: 'zoneStart', label: 'Zone Début' },
  { key: 'zoneEnd', label: 'Zone Fin' },
  { key: 'zonePause', label: 'Zone(s) Pause(s)' },
  { key: 'device', label: '📱 Appareil' },
  { key: 'info', label: 'Info' },
];

function AdminDashboard() {
  const [workSessions, setWorkSessions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesHistory, setSitesHistory] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  // Mois en cours par défaut (format YYYY-MM)
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [newAbsenceRequests, setNewAbsenceRequests] = useState(0);
  const [pendingEscalations, setPendingEscalations] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false); // Nouvel état pour bloquer le bouton
  const siteCache = {};
  // Valeur par défaut pour le temps de session max (en heures)
  const [maxSessionTime, setMaxSessionTime] = useState(12); // 12h par défaut

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Bandeau de validation de feuille de temps
  const timesheetId = searchParams.get('timesheetId');
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [timesheetData, setTimesheetData] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [employerComment, setEmployerComment] = useState('');

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getTimestampInMs = (timestamp) => {
    if (!timestamp) return 0;
    if (typeof timestamp === 'number') {
      // Timestamp en secondes ou millisecondes
      return timestamp > 1e12 ? timestamp : timestamp * 1000;
    }
    if (timestamp.seconds !== undefined) {
      // Firestore Timestamp
      return timestamp.seconds * 1000;
    }
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
    return 0;
  };

  const getAuthorizedSitesAtDate = (employeeEmail, timestamp) => {
    if (!timestamp) {
      console.log('🔍 DEBUG - Pas de timestamp fourni');
      return [];
    }

    // Convertir le timestamp en Date pour la comparaison
    const eventTimeMs = getTimestampInMs(timestamp);
    const eventDate = new Date(eventTimeMs);
    console.log('🔍 DEBUG - Date de l\'événement:', eventDate.toLocaleString('fr-FR'));
    
    // Pour Clément TEST uniquement
    const isClementTest = employeeEmail === "bonjour@bonjour.com";
    
    // Historique complet pour Clément
    if (isClementTest) {
      console.log('🔍 DEBUG - Vérification pour Clément TEST');
      console.log('🔍 DEBUG - Sites disponibles:', sites.map(s => ({ id: s.id, nom: s.nom })));
      console.log('🔍 DEBUG - Historique complet:', sitesHistory.filter(h => h.employeeEmail === "bonjour@bonjour.com"));
    }

    // Trouver la dernière modification AVANT la création de la session ou de la pause
    const relevantHistory = sitesHistory
      .filter(history => {
        const hasEmail = history.employeeEmail?.trim() === employeeEmail?.trim();
        const hasChangeDate = history.changeDate != null;
        const changeDateMs = hasChangeDate ? getTimestampInMs(history.changeDate) : 0;
        if (isClementTest) {
          console.log('🔍 DEBUG - Vérification entrée historique:', {
            email: history.employeeEmail,
            hasEmail,
            hasChangeDate,
            changeDate: hasChangeDate ? new Date(changeDateMs).toLocaleString('fr-FR') : null,
            dateModification: changeDateMs,
            datePointage: eventTimeMs,
            estAvantPointage: changeDateMs < eventTimeMs
          });
        }
        return hasEmail && hasChangeDate;
      })
      .map(history => ({
        ...history,
        changeDateObj: new Date(getTimestampInMs(history.changeDate))
      }))
      .filter(history => {
        // Ne prendre que les modifications faites avant l'événement (session ou pause)
        const isBeforeSession = history.changeDateObj.getTime() < eventTimeMs;
        if (isClementTest) {
          console.log('🔍 DEBUG - Comparaison dates:', {
            changeDate: history.changeDateObj.toLocaleString('fr-FR'),
            eventDate: eventDate.toLocaleString('fr-FR'),
            isBeforeSession
          });
        }
        return isBeforeSession;
      })
      .sort((a, b) => b.changeDateObj - a.changeDateObj);

    if (isClementTest) {
      console.log('🔍 DEBUG - Historique pertinent trouvé:', relevantHistory);
    }

    if (relevantHistory.length > 0) {
      const siteIds = relevantHistory[0].newSites;
      
      if (isClementTest) {
        console.log('🔍 DEBUG - Sites autorisés à', eventDate.toLocaleString('fr-FR'), ':', 
          siteIds.map(id => sites.find(s => s.id === id)?.nom || id)
        );
      }
      
      return siteIds;
    } else {
      // Si pas d'historique antérieur à la session, utiliser les sites de l'employé
      const emp = employees.find(e => e.email === employeeEmail);
      const currentSites = emp ? (Array.isArray(emp.siteDeTravail) ? emp.siteDeTravail : [emp.siteDeTravail]) : [];
      
      if (isClementTest) {
        console.log('🔍 DEBUG - Aucun historique antérieur trouvé, utilisation des sites initiaux:', 
          currentSites.map(id => sites.find(s => s.id === id)?.nom || id)
        );
      }
      
      return currentSites;
    }
  };

  // Utilitaire pour fallback sur lat/lng si latitude/longitude absents
  function getLat(location) {
    return location?.latitude !== undefined ? location.latitude : location?.lat;
  }
  function getLng(location) {
    return location?.longitude !== undefined ? location.longitude : location?.lng;
  }

  const findPositionSite = (location) => {
    if (!location || !location.latitude || !location.longitude) {
      return null;
    }
    
    // Chercher parmi tous les sites existants
    const foundSite = sites.find(site => {
      if (!site.latitude || !site.longitude || !site.rayon) {
        return false;
      }
      
      const dist = calculateDistance(site.latitude, site.longitude, location.latitude, location.longitude);
      
      return dist <= site.rayon;
    });

    return foundSite;
  };

  const checkZone = (location, employeeEmail, timestamp, sessionSiteId) => {
    if (!location || !location.latitude || !location.longitude || !timestamp) {
      return false;
    }

    const isClementTest = employeeEmail === "bonjour@bonjour.com";
    
    // Obtenir les sites autorisés à la date donnée
    const authorizedSites = getAuthorizedSitesAtDate(employeeEmail, timestamp);
    
    // Log uniquement pour Clément TEST
    if (isClementTest) {
      console.log(`🔍 DEBUG - Sites autorisés pour le pointage:`, {
        sitesAutorisés: authorizedSites.map(id => sites.find(s => s.id === id)?.nom || id)
      });
    }

    // Trouver le site où l'employé se trouve physiquement
    const actualSite = findPositionSite(location);
    
    if (isClementTest) {
      console.log(`🔍 DEBUG - Site détecté par position:`, actualSite?.nom || "Aucun");
    }

    // Si l'employé est dans un site autorisé, le pointage est valide
    if (actualSite && authorizedSites.includes(actualSite.id)) {
      if (isClementTest) {
        console.log(`🔍 DEBUG - Position validée:`, {
          sitePhysique: actualSite.nom,
          estAutorisé: true
        });
      }
      return true;
    }

    if (isClementTest) {
      console.log(`🔍 DEBUG - Position non validée:`, {
        sitePhysique: actualSite?.nom || "Hors zone",
        sitesAutorisés: authorizedSites.map(id => sites.find(s => s.id === id)?.nom || id)
      });
    }
    
    return false;
  };

  const formatGPSCoordinate = (location, coordinate) => {
    if (!location) return "Non disponible";
    if (coordinate === "latitude") return getLat(location) ?? "Non disponible";
    if (coordinate === "longitude") return getLng(location) ?? "Non disponible";
    return "Non disponible";
  };

  const fetchWorkSessions = async (monthFilter = selectedMonth) => {
    try {
      console.log("Fetching work sessions for month:", monthFilter);
      
      // Parser le mois sélectionné (format YYYY-MM)
      const [year, month] = monthFilter.split('-').map(Number);
      
      // Calculer le début et la fin du mois sélectionné
      const startOfMonth = new Date(year, month - 1, 1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      const endOfMonth = new Date(year, month, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      
      console.log('📅 Chargement des sessions du mois:', {
        mois: monthFilter,
        debut: startOfMonth.toLocaleDateString('fr-FR'),
        fin: endOfMonth.toLocaleDateString('fr-FR')
      });
      
      // Récupérer UNIQUEMENT les sessions du mois sélectionné
      const sessionsSnapshot = await getDocs(
        query(
          collection(db, "workSessions"),
          where("startTime", ">=", Timestamp.fromDate(startOfMonth)),
          where("startTime", "<=", Timestamp.fromDate(endOfMonth)),
          orderBy("startTime", "desc")
        )
      );
      const sessionDocs = sessionsSnapshot.docs;
      
      console.log(`✅ ${sessionDocs.length} sessions chargées pour ${monthFilter}`);

      // Récupérer toutes les pauses en une seule requête
      const breaksSnapshot = await getDocs(collection(db, "breaks"));
      const allBreaks = breaksSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });

      // Organiser les pauses par sessionId
      const breaksBySession = allBreaks.reduce((acc, breakItem) => {
        if (!acc[breakItem.sessionId]) {
          acc[breakItem.sessionId] = [];
        }
        acc[breakItem.sessionId].push(breakItem);
        return acc;
      }, {});

      // Combiner les sessions avec leurs pauses respectives
      const sessionsData = sessionDocs.map(doc => {
        const sessionData = doc.data();
        const sessionBreaks = breaksBySession[doc.id] || [];
        return {
          id: doc.id,
          ...sessionData,
          breaks: sessionBreaks
        };
      });

      // Trier les sessions par date de début (la plus récente en premier)
      const sortedSessions = sessionsData.sort((a, b) => {
        const getStartTime = (session) => {
          if (session.offlineStartTime) {
            return new Date(session.offlineStartTime).getTime();
          }
          if (session.startTime) {
            return session.startTime.toDate().getTime();
          }
          if (session.transmissionStartTime) {
            return session.transmissionStartTime.toDate().getTime();
          }
          return 0;
        };

        return getStartTime(b) - getStartTime(a);
      });

      console.log("Work sessions loaded:", sortedSessions.length);
      setWorkSessions(sortedSessions);
    } catch (error) {
      console.error("Error fetching work sessions:", error);
      setError("Erreur lors du chargement des sessions de travail");
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("AdminDashboard mounted");
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log("Auth state changed:", user ? "logged in" : "logged out");
      if (user) {
        try {
          setLoading(true);
          setError(null);
          
          // Exécuter toutes les requêtes en parallèle
          await Promise.all([
            fetchEmployees(),
            fetchSites(),
            fetchSitesHistory(),
            fetchWorkSessions()
          ]);
          
          setLoading(false);
        } catch (err) {
          console.error("Error loading data:", err);
          setError("Erreur lors du chargement des données");
          setLoading(false);
        }
      } else {
        console.log("Redirecting to login");
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // UseEffect pour recharger les sessions quand le mois sélectionné change
  useEffect(() => {
    if (!loading && employees.length > 0 && sites.length > 0) {
      console.log('🔄 Rechargement des sessions pour le mois:', selectedMonth);
      fetchWorkSessions(selectedMonth);
    }
  }, [selectedMonth]); // Se déclenche uniquement quand selectedMonth change

  // UseEffect pour gérer le mode validation de feuille de temps
  useEffect(() => {
    const loadTimesheetForValidation = async () => {
      if (!timesheetId) {
        setShowValidationBanner(false);
        return;
      }

      try {
        console.log('📋 Chargement de la feuille de temps:', timesheetId);
        
        // Charger la feuille depuis Firebase
        const timesheetDoc = await getDoc(doc(db, 'workSessionsMonth', timesheetId));
        
        if (!timesheetDoc.exists()) {
          console.error('❌ Feuille de temps introuvable');
          return;
        }

        const data = timesheetDoc.data();
        setTimesheetData({ id: timesheetDoc.id, ...data });
        setEmployerComment(data.employerComment || '');
        
        // Appliquer les filtres automatiquement
        const employeeEmail = searchParams.get('employee');
        const month = searchParams.get('month');
        
        if (employeeEmail) {
          setSelectedEmployee(employeeEmail);
          console.log('🔍 Filtre employé appliqué:', employeeEmail);
        }
        
        if (month) {
          setSelectedMonth(month);
          console.log('🔍 Filtre mois appliqué:', month);
        }
        
        setShowValidationBanner(true);
        console.log('✅ Mode validation activé');
        
      } catch (error) {
        console.error('❌ Erreur chargement feuille:', error);
      }
    };

    loadTimesheetForValidation();
  }, [timesheetId, searchParams]);

  const fetchEmployees = async () => {
    console.log("Fetching employees...");
    const snapshot = await getDocs(collection(db, "employees"));
    const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    console.log("Employees loaded:", list.length);
    setEmployees(list);
  };

  const fetchSites = async () => {
    console.log("Fetching sites...");
    const snapshot = await getDocs(collection(db, "sites"));
    const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    console.log("Sites loaded:", list.length);
    setSites(list);
  };

  const fetchSitesHistory = async () => {
    try {
      const sitesSnap = await getDocs(collection(db, "sitesHistory"));
      const sitesData = sitesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSitesHistory(sitesData);
    } catch (error) {
      console.error("Erreur lors de la récupération des sites:", error);
    }
  };

  const fetchNewAbsenceRequests = async () => {
    try {
      const absencesRef = collection(db, "absences");
      const q = query(absencesRef, where("status", "==", "En attente"));
      const snapshot = await getDocs(q);
      setNewAbsenceRequests(snapshot.size);
    } catch (error) {
      console.error("Erreur lors du chargement des demandes d'absence:", error);
    }
  };

  const fetchPendingEscalations = async () => {
    try {
      const escalationsRef = collection(db, 'adminEscalations');
      const q = query(escalationsRef, where('status', '==', 'pending_admin_review'));
      const snapshot = await getDocs(q);
      setPendingEscalations(snapshot.size);
    } catch (error) {
      console.error('Erreur lors du chargement du compteur d\'escalades:', error);
    }
  };

  const formatTime = (ms) => {
    if (!ms || isNaN(ms)) return '0h 0m';
    const totalMinutes = Math.round(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatTimeHHMM = (timestamp) => {
    if (!timestamp) return '';
    if (typeof timestamp === 'number') {
      // Si c'est un timestamp en secondes
      const date = new Date(timestamp * 1000);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } else if (timestamp.seconds !== undefined) {
      // Si c'est un timestamp Firestore
      const date = new Date(timestamp.seconds * 1000);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } else if (timestamp.toDate) {
      // Si c'est un timestamp Firestore avec méthode toDate()
      const date = timestamp.toDate();
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
    return '';
  };

  const getTimeInSeconds = (session) => {
    if (session.offlineStartTime) {
      return Math.floor(new Date(session.offlineStartTime).getTime() / 1000);
    }
    if (session.startTime) {
      return session.startTime.seconds;
    }
    return 0;
  };

  const filteredSessions = workSessions
    .filter((session) => {
      if (selectedEmployee && session.employeeEmail !== selectedEmployee) return false;

      // Obtenir le timestamp en secondes
      const startTimeSeconds = getTimeInSeconds(session);

      if (!startTimeSeconds) {
        console.warn('Session sans timestamp de début valide:', session);
        return false;
      }

      // Plus besoin de filtrer par mois ici car c'est fait lors du chargement depuis Firebase
      return true;
    })
    .sort((a, b) => getTimeInSeconds(b) - getTimeInSeconds(a));

  // Pagination sur les sessions filtrées
  const pagedSessions = filteredSessions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalSessions = filteredSessions.length;

  const exportToCSV = async () => {
    try {
      // Récupérer la configuration CSV depuis Firestore
      const configRef = doc(db, "adminConfigs", "global");
      const configSnap = await getDoc(configRef);
      let csvColumns;
      
      if (configSnap.exists()) {
        const data = configSnap.data();
        csvColumns = data.csvColumns || ADMIN_COLUMNS.map(col => ({ ...col, checked: true }));
      } else {
        csvColumns = ADMIN_COLUMNS.map(col => ({ ...col, checked: true }));
      }

      // Déclarer keyMapping ici
      const keyMapping = {
        'nom': 'name',
        'date': 'date',
        'heureDebut': 'startTime',
        'heureFin': 'endTime',
        'travail': 'workTime',
        'pause': 'pauseTime',
        'site': 'sites',
        'gpsDebut': 'gpsStart',
        'gpsFin': 'gpsEnd',
        'zoneDebut': 'zoneStart',
        'zoneFin': 'zoneEnd',
        'zonePause': 'zonePause',
        'appareil': 'device',
        'info': 'info'
      };

      const rows = [];
      // En-tête avec uniquement les colonnes sélectionnées
      const header = [];
      csvColumns.forEach(col => {
        if (col.checked) {
          header.push(col.label);
        }
      });
      rows.push(header);

      pagedSessions.forEach((session) => {
        const row = [];
        csvColumns.forEach(col => {
          if (!col.checked) return;
          const englishKey = keyMapping[col.key] || col.key;
          const startTime = session.startTime?.seconds ? new Date(session.startTime.seconds * 1000) : null;
          const endTime = session.endTime?.seconds ? new Date(session.endTime.seconds * 1000) : null;
          switch (englishKey) {
            case 'name':
              // Utiliser la nouvelle fonction pour le nom
              row.push(getEmployeeDisplayName(session));
              break;
            case 'date':
              row.push(startTime ? startTime.toLocaleDateString('fr-FR') : 'Non disponible');
              break;
            case 'startTime':
              row.push(startTime ? startTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'Non disponible');
              break;
            case 'endTime':
              row.push(endTime ? endTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'En cours');
              break;
            case 'workTime':
              if (!startTime || !endTime) row.push('Non disponible');
              else {
                row.push(getWorkDuration(session));
              }
              break;
            case 'pauseTime':
              row.push(getPauseDuration(session));
              break;
            case 'sites':
              // Utiliser la nouvelle fonction pour les sites autorisés
              row.push(getEmployeeAuthorizedSites(session));
              break;
            case 'gpsStart':
              if (!session.startLocation) row.push('Non disponible');
              else row.push(`Lat: ${formatGPSCoordinate(session.startLocation, "latitude")}, Long: ${formatGPSCoordinate(session.startLocation, "longitude")}`);
              break;
            case 'gpsEnd':
              if (!session.endLocation) row.push('Non disponible');
              else row.push(`Lat: ${formatGPSCoordinate(session.endLocation, "latitude")}, Long: ${formatGPSCoordinate(session.endLocation, "longitude")}`);
              break;
            case 'zoneStart':
              row.push(getZoneLabelSimple(session.startLocation, sites));
              break;
            case 'zoneEnd':
              row.push(getZoneLabelSimple(session.endLocation, sites));
              break;
            case 'zonePause':
              row.push(getPauseZonesForExport(session, sites));
              break;
            case 'device':
              row.push(formatDeviceInfo(session.device));
              break;
            case 'info':
              row.push(session.isManualAdjustment || session.isEscalationValidated ? 'Manuel' : 'Auto');
              break;
            default:
              row.push("");
          }
        });
        rows.push(row);
      });

      // Calculer les totaux
      const totalWorkTime = formatTime(
        pagedSessions.reduce((total, session) => {
          if (session.adjustedWorkTime != null) {
            return total + session.adjustedWorkTime;
          }
          if (session.startTime?.seconds && session.endTime?.seconds) {
            return total + Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
          }
          return total;
        }, 0)
      );

      const totalPauseTime = formatTime(
        pagedSessions.reduce((total, session) => 
          total + (session.totalPauseTime || 0), 0
        )
      );

      // Ajouter la ligne de total
      const totalRow = [];
      csvColumns.forEach(col => {
        if (!col.checked) return;
        const englishKey = keyMapping[col.key] || col.key;
        switch (englishKey) {
          case 'name':
            totalRow.push(`TOTAL (${pagedSessions.length} sessions)`);
            break;
          case 'workTime':
            totalRow.push(totalWorkTime);
            break;
          case 'pauseTime':
            totalRow.push(totalPauseTime);
            break;
          default:
            totalRow.push("-");
        }
      });
      rows.push(totalRow);

      // Ajout du BOM UTF-8 pour Excel
      const csvContent = "\uFEFF" + rows.map((r) => r.join(";")).join("\n");
      const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "suivi_salaries.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur lors de l'export CSV:", error);
      alert(`Une erreur est survenue lors de l'export CSV: ${error.message}`);
    }
  };

  // Fonction utilitaire pour charger une image en DataURL et obtenir ses dimensions
  async function loadImageData(url) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        // Créer un canvas pour convertir en DataURL
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        resolve({ dataUrl, width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  // Versions "texte" pour le PDF (même logique que getStartTimeDisplay/getEndTimeDisplay)
  const getStartTimeDisplayText = (session) => {
    const real = session.startTime?.seconds ? formatTimeHHMM(session.startTime) : '';
    const adjusted = session.adjustedStartTime ? formatTimeHHMM(session.adjustedStartTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedStartTime) {
      return adjusted;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real && real) {
      return `${adjusted} / ${real}`;
    } else {
      return real || 'Non disponible';
    }
  };

  const getEndTimeDisplayText = (session) => {
    const real = session.endTime?.seconds ? formatTimeHHMM(session.endTime) : 'En cours';
    const adjusted = session.adjustedEndTime ? formatTimeHHMM(session.adjustedEndTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedEndTime) {
      return adjusted;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real && real !== 'En cours') {
      return `${adjusted} / ${real}`;
    } else {
      return real;
    }
  };

  const exportToPDF = async () => {
    try {
      // Récupérer la configuration PDF depuis Firestore
      const configRef = doc(db, "adminConfigs", "global");
      const configSnap = await getDoc(configRef);
      let pdfColumns;
      let pdfShowHoraeLogo = false;
      
      if (configSnap.exists()) {
        const data = configSnap.data();
        pdfColumns = data.pdfColumns || ADMIN_COLUMNS.map(col => ({ ...col, checked: true, width: Math.round(100 / ADMIN_COLUMNS.length) }));
        pdfShowHoraeLogo = !!data.pdfShowHoraeLogo;
      } else {
        pdfColumns = ADMIN_COLUMNS.map(col => ({ ...col, checked: true, width: Math.round(100 / ADMIN_COLUMNS.length) }));
      }

      const pdfOrientation = configSnap.exists() && configSnap.data().pdfOrientation 
        ? configSnap.data().pdfOrientation 
        : 'landscape';

      console.log("Configuration PDF utilisée:", pdfColumns); // Debug

      // Passer l'orientation à "landscape" pour le PDF
      const pdfDoc = new jsPDF({ orientation: pdfOrientation });
      
      // En-tête du document
      pdfDoc.setFontSize(18);
      pdfDoc.text("Rapport de pointage", 14, 35);

      // Afficher le logo HORAE si demandé
      if (pdfShowHoraeLogo) {
        try {
          const imgData = await loadImageData('/logo.png');
          // Limite la hauteur à 6mm, largeur proportionnelle
          const maxH = 6;
          const ratio = imgData.width / imgData.height;
          const h = maxH;
          const w = Math.round(h * ratio);
          const pageWidth = pdfDoc.internal.pageSize.getWidth();
          const marginRight = 14;
          const x = pageWidth - marginRight - w;
          await pdfDoc.addImage(imgData.dataUrl, 'PNG', x, 10, w, h, undefined, 'FAST'); // coin haut droit
        } catch (e) {
          console.warn('Logo HORAE non trouvé ou erreur lors de l\'ajout du logo HORAE');
        }
      }
      
      // Informations de filtrage
      pdfDoc.setFontSize(12);
      
      // Convertir YYYY-MM en format français
      const [year, monthNum] = selectedMonth.split('-');
      const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      const monthLabel = `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      
      let filterText = `Période : ${monthLabel}`;
      if (selectedEmployee === 'all') {
        filterText += `\nEmployé : Tous`;
      } else {
        const emp = employees.find(e => e.email === selectedEmployee);
        if (emp) {
          filterText += `\nEmployé : ${emp.prenom ? emp.prenom + ' ' : ''}${emp.nom || selectedEmployee}`;
        } else {
          filterText += `\nEmployé : ${selectedEmployee}`;
        }
      }
      pdfDoc.text(filterText, 14, 50);
      
      // Saut de ligne avant le tableau pour aérer
      let currentY = 60; // startY élevé pour la première page

      // Mapping des clés françaises vers anglaises
      const keyMapping = {
        'nom': 'name',
        'date': 'date',
        'heureDebut': 'startTime',
        'heureFin': 'endTime',
        'travail': 'workTime',
        'pause': 'pauseTime',
        'site': 'sites',
        'gpsDebut': 'gpsStart',
        'gpsFin': 'gpsEnd',
        'zoneDebut': 'zoneStart',
        'zoneFin': 'zoneEnd',
        'appareil': 'device',
        'info': 'info'
      };

      // 1. Colonnes cochées et ordre
      const checkedColumns = pdfColumns.filter(col => col.checked);

      // 2. En-tête avec labels personnalisés pour le PDF
      const columns = checkedColumns.map(col => {
        // Ajouter "(Ret./Réel)" pour les colonnes d'horaires (version courte)
        if (col.key === 'startTime') {
          return 'H. Début (Ret./Réel)';
        } else if (col.key === 'endTime') {
          return 'H. Fin (Ret./Réel)';
        }
        return col.label;
      });

      // 3. Styles de colonnes (largeur en % du total, convertie en mm)
      const pageWidth = pdfDoc.internal.pageSize.getWidth() - 28; // 14mm de marge de chaque côté
      const columnStyles = {};
      checkedColumns.forEach((col, idx) => {
        columnStyles[idx] = { cellWidth: (col.width / 100) * pageWidth };
      });

      // 4. Lignes du tableau
      const rows = pagedSessions.map(session => {
        return checkedColumns.map(col => {
          // Convertir la clé française en clé anglaise
          const englishKey = keyMapping[col.key] || col.key;
          
          // Vérification de sécurité pour les données de session
          const startTime = session.startTime?.seconds ? new Date(session.startTime.seconds * 1000) : null;
          const endTime = session.endTime?.seconds ? new Date(session.endTime.seconds * 1000) : null;
          
          switch (englishKey) {
            case 'name':
              // Utiliser la nouvelle fonction pour le nom
              return getEmployeeDisplayName(session);
            case 'date':
              return startTime ? startTime.toLocaleDateString('fr-FR') : 'Non disponible';
            case 'startTime':
              return getStartTimeDisplayText(session);
            case 'endTime':
              return getEndTimeDisplayText(session);
            case 'workTime':
              if (!startTime || !endTime) return 'Non disponible';
              return getWorkDuration(session);
            case 'pauseTime':
              return getPauseDuration(session);
            case 'sites':
              // Utiliser la nouvelle fonction pour les sites autorisés
              return getEmployeeAuthorizedSites(session);
            case 'gpsStart':
              if (!session.startLocation) return 'Non disponible';
              return `Lat: ${formatGPSCoordinate(session.startLocation, "latitude")}, Long: ${formatGPSCoordinate(session.startLocation, "longitude")}`;
            case 'gpsEnd':
              if (!session.endLocation) return 'Non disponible';
              return `Lat: ${formatGPSCoordinate(session.endLocation, "latitude")}, Long: ${formatGPSCoordinate(session.endLocation, "longitude")}`;
            case 'zoneStart':
              return getZoneLabelSimple(session.startLocation, sites);
            case 'zoneEnd':
              return getZoneLabelSimple(session.endLocation, sites);
            case 'zonePause':
              return getPauseZonesForExport(session, sites);
            case 'device':
              return formatDeviceInfo(session.device);
            case 'info':
              return session.isManualAdjustment || session.isEscalationValidated ? 'Manuel' : 'Auto';
            default:
              return 'Non disponible';
          }
        });
      });

      // 5. Calculer les totaux
      const totalWorkTime = formatTime(
        pagedSessions.reduce((total, session) => {
          // Utiliser la même logique que getWorkDuration
          if (session.adjustedWorkTime != null) {
            return total + session.adjustedWorkTime;
          }
          if (session.adminModified && session.adjustedStartTime && session.adjustedEndTime) {
            const startTime = session.adjustedStartTime instanceof Date ? session.adjustedStartTime.getTime() : new Date(session.adjustedStartTime).getTime();
            const endTime = session.adjustedEndTime instanceof Date ? session.adjustedEndTime.getTime() : new Date(session.adjustedEndTime).getTime();
            return total + Math.max(0, endTime - startTime - (session.totalPauseTime || 0));
          }
          if (session.startTime?.seconds && session.endTime?.seconds) {
            return total + Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
          }
          return total;
        }, 0)
      );

      const totalPauseTime = formatTime(
        pagedSessions.reduce((total, session) => 
          total + (session.totalPauseTime || 0), 0
        )
      );

      // 6. Ajouter la ligne de totaux
      const totalRow = checkedColumns.map(col => {
        const englishKey = keyMapping[col.key] || col.key;
        switch (englishKey) {
          case 'name':
            return `TOTAL (${pagedSessions.length} sessions)`;
          case 'workTime':
            return totalWorkTime;
          case 'pauseTime':
            return totalPauseTime;
          default:
            return "-";
        }
      });
      rows.push(totalRow);

      autoTable(pdfDoc, {
        head: [columns],
        body: rows,
        startY: currentY,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 8 }, // Réduit de 9 à 8
        footStyles: { fillColor: [233, 236, 239], textColor: 0, fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 10 },
        theme: 'grid',
        tableWidth: 'auto',
        columnStyles,
        horizontal: 'center',
        didParseCell: function (data) {
          // Si c'est la dernière ligne du body (totaux), on met en gras
          if (data.section === 'body' && data.row.index === rows.length - 1) {
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      // Sauvegarder le PDF
      pdfDoc.save("suivi_pointages.pdf");
    } catch (error) {
      console.error("Erreur lors de l'export PDF:", error);
      alert(`Une erreur est survenue lors de l'export PDF: ${error.message}`);
    }
  };

  // Générer la liste des 12 derniers mois (fixe, indépendamment des sessions)
  const months = (() => {
    const monthsList = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
      monthsList.push(monthKey);
    }
    
    return monthsList;
  })();

  const getWorkDuration = (session) => {
    // PRIORITÉ 1 : Utiliser adjustedWorkTime s'il existe (calculé lors de la sauvegarde)
    if (session.adjustedWorkTime != null) {
      return formatTime(session.adjustedWorkTime);
    }
    
    // PRIORITÉ 2 : Si l'horaire a été modifié manuellement mais pas de adjustedWorkTime, recalculer
    if (session.adminModified && session.adjustedStartTime && session.adjustedEndTime) {
      const startTime = session.adjustedStartTime instanceof Date ? session.adjustedStartTime.getTime() : new Date(session.adjustedStartTime).getTime();
      const endTime = session.adjustedEndTime instanceof Date ? session.adjustedEndTime.getTime() : new Date(session.adjustedEndTime).getTime();
      const workTimeMs = Math.max(0, endTime - startTime - (session.totalPauseTime || 0));
      return formatTime(workTimeMs);
    }
    
    // PRIORITÉ 3 : Utiliser les horaires originaux
    if (session.startTime?.seconds && session.endTime?.seconds) {
      const workTimeMs = Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
      return formatTime(workTimeMs);
    }
    return '0h 0m';
  };

  const getPauseDuration = (session) => {
    return formatTime(session.totalPauseTime || 0);
  };

  const getStartTimeDisplay = (session) => {
    const real = session.startTime?.seconds ? formatTimeHHMM(session.startTime) : '';
    const adjusted = session.adjustedStartTime ? formatTimeHHMM(session.adjustedStartTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedStartTime) {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real) {
      return (
        <>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>
          <div style={{ color: '#444', fontSize: '0.85em' }}>{real}</div>
        </>
      );
    } else {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{real}</div>;
    }
  };

  const getEndTimeDisplay = (session) => {
    const real = session.endTime?.seconds ? formatTimeHHMM(session.endTime) : 'En cours';
    const adjusted = session.adjustedEndTime ? formatTimeHHMM(session.adjustedEndTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedEndTime) {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real) {
      return (
        <>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>
          <div style={{ color: '#444', fontSize: '0.85em' }}>{real}</div>
        </>
      );
    } else {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{real}</div>;
    }
  };

  // Correction de l'affichage GPS et zones
  const getGPSLink = (location) => {
    const lat = getLat(location);
    const lng = getLng(location);
    if (!lat || !lng) return <span className="no-location">Non disponible</span>;
    return (
      <a
        href={`https://www.google.com/maps?q=${lat},${lng}`}
        target="_blank"
        rel="noopener noreferrer"
        className="map-link"
      >
        {`Lat: ${lat}, Long: ${lng}`}
      </a>
    );
  };

  const getCoord = (location) => {
    if (!location) return null;
    if (typeof location.lat === 'number' && typeof location.lng === 'number') {
      return { lat: location.lat, lng: location.lng };
    }
    if (typeof location.latitude === 'number' && typeof location.longitude === 'number') {
      return { lat: location.latitude, lng: location.longitude };
    }
    return null;
  };

  const getBreakLocations = (session) => {
    if (!session.breaks || session.breaks.length === 0) return "Aucune pause";

    // Trie décroissant (plus récent en haut)
    const sortedBreaks = [...session.breaks].sort((a, b) => {
      const aTime = a.startTime?.toDate ? a.startTime.toDate().getTime() : new Date(a.startTime).getTime();
      const bTime = b.startTime?.toDate ? b.startTime.toDate().getTime() : new Date(b.startTime).getTime();
      return bTime - aTime;
    });

    return sortedBreaks.map(breakItem => {
      const startCoord = getCoord(breakItem.startLocation);
      const endCoord = getCoord(breakItem.endLocation);
      const startTime = breakItem.startTime?.toDate ? breakItem.startTime.toDate() : new Date(breakItem.startTime);
      const endTime = breakItem.endTime?.toDate ? breakItem.endTime.toDate() : new Date(breakItem.endTime);
      
      const formatLocation = (coord, time, label, siteId) => {
        let lieu = null;
        let colorClass = '';
        let isHorsZone = false;
        let lien = null;
        let site = null;
        
        // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
        if (siteId) {
          // Cas spécial : HORS_ZONE
          if (siteId === 'HORS_ZONE') {
            lieu = "HORS ZONE";
            colorClass = 'zone-ko';
            isHorsZone = true;
          } else {
            // Cas normal : chercher le site correspondant
            site = sites.find(s => s.id === siteId);
            if (site) {
              lieu = site.nom;
              colorClass = 'zone-ok';
            }
          }
        }
        
        // PRIORITÉ 2 : Si pas de siteId ou site non trouvé (et pas HORS_ZONE), calculer à partir des coordonnées
        if (!site && !isHorsZone && coord) {
          site = findPositionSite({ latitude: coord.lat, longitude: coord.lng });
          if (site) {
            lieu = site.nom;
            colorClass = 'zone-ok';
          }
        }
        
        // Si toujours pas de site trouvé et pas HORS_ZONE
        if (!site && !isHorsZone) {
          lieu = "HORS ZONE";
          colorClass = 'zone-ko';
          isHorsZone = true;
        }
        
        // Lien carte uniquement pour HORS ZONE
        if (isHorsZone && coord) {
          lien = (
            <a 
              href={`https://www.google.com/maps?q=${coord.lat},${coord.lng}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="zone-link"
              style={{ 
                display: 'block',
                margin: '0 auto',
                textAlign: 'center',
                color: '#1976d2', 
                textDecoration: 'none', 
                fontWeight: 'normal',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            >
              📍Carte
            </a>
          );
        }
        
        return (
          <div style={{ textAlign: 'center' }}>
            <div>
              <span style={{ 
                color: colorClass === 'zone-ok' ? '#4caf50' : 
                       colorClass === 'zone-ko' ? '#f44336' : '#ff9800',
                fontWeight: 'bold'
              }}>
                {label} {time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div style={{ color: colorClass === 'zone-ok' ? '#4caf50' : colorClass === 'zone-ko' ? '#f44336' : '#ff9800' }}>
              {lieu}
            </div>
            {lien && (
              <div style={{ textAlign: 'center' }}>
                {lien}
              </div>
            )}
          </div>
        );
      };

      return (
        <div key={breakItem.id} className="break-location">
          <div>{formatLocation(startCoord, startTime, 'D', breakItem.startLocation?.siteId)}</div>
          <div>{formatLocation(endCoord, endTime, 'F', breakItem.endLocation?.siteId)}</div>
        </div>
      );
    });
  };

  // Fonction pour obtenir le nom correct de l'employé (prénom nom)
  const getEmployeeDisplayName = (session) => {
    const employee = employees.find(emp => emp.email === session.employeeEmail);
    if (employee) {
      return `${employee.prenom} ${employee.nom}`;
    }
    return session.employeeName || session.employeeEmail || "Inconnu";
  };

  // Fonction pour obtenir tous les sites autorisés de l'employé
  const getEmployeeAuthorizedSites = (session) => {
    const employee = employees.find(emp => emp.email === session.employeeEmail);
    if (employee && employee.siteDeTravail) {
      const authorizedSiteIds = Array.isArray(employee.siteDeTravail) ? employee.siteDeTravail : [employee.siteDeTravail];
      const authorizedSites = authorizedSiteIds
        .map(siteId => {
          const site = sites.find(s => s.id === siteId);
          return site ? site.nom : "Site inconnu";
        })
        .join(", ");
      return authorizedSites;
    }
    return "Non défini";
  };

  // Fonction pour obtenir le site de début/fin
  const getSessionSite = (session, type) => {
    console.log(`🎯 SITE_DEBUG getSessionSite appelé avec type: ${type}`);
    console.log(`🎯 SITE_DEBUG session.startLocation:`, session.startLocation);
    console.log(`🎯 SITE_DEBUG session.endLocation:`, session.endLocation);
    
    if (type === 'start') {
      const siteId = session.startLocation?.siteId || session.siteDeTravail;
      console.log(`🎯 SITE_DEBUG start - siteId: ${siteId}`);
      if (siteId === 'HORS_ZONE') {
        console.log(`🎯 SITE_DEBUG start - HORS_ZONE détecté, retour: HORS ZONE`);
        return 'HORS ZONE';
      }
      const site = sites.find(s => s.id === siteId);
      const result = site ? site.nom : siteId || 'Non disponible';
      console.log(`🎯 SITE_DEBUG start - résultat: ${result}`);
      return result;
    } else if (type === 'end') {
      const siteId = session.endLocation?.siteId || session.endSite;
      console.log(`🎯 SITE_DEBUG end - siteId: ${siteId}`);
      if (siteId === 'HORS_ZONE') {
        console.log(`🎯 SITE_DEBUG end - HORS_ZONE détecté, retour: HORS ZONE`);
        return 'HORS ZONE';
      }
      const site = sites.find(s => s.id === siteId);
      const result = site ? site.nom : siteId || 'Non disponible';
      console.log(`🎯 SITE_DEBUG end - résultat: ${result}`);
      return result;
    }
    return 'Non disponible';
  };

  // Fonction utilitaire pour afficher la zone (début/fin) avec couleur et lien si hors zone
  const renderZoneCell = (location, employee, sites, label) => {
    if (!location) return <span style={{ color: 'red' }}>Non disponible</span>;
    
    // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
    if (location.siteId) {
      // Cas spécial : HORS_ZONE
      if (location.siteId === 'HORS_ZONE') {
        const lat = getLat(location);
        const lng = getLng(location);
        return (
          <div style={{ color: 'red', fontWeight: 'bold' }}>
            HORS ZONE
            {lat && lng && (
              <div>
                <a
                  href={`https://www.google.com/maps?q=${lat},${lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#1976d2', 
                    textDecoration: 'none', 
                    fontWeight: 'normal',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  📍Carte
                </a>
              </div>
            )}
          </div>
        );
      }
      
      // Cas normal : chercher le site correspondant
      const site = sites.find(s => s.id === location.siteId);
      if (site) {
        const authorizedSiteIds = employee && employee.siteDeTravail
          ? (Array.isArray(employee.siteDeTravail) ? employee.siteDeTravail : [employee.siteDeTravail])
          : [];
        const isAuthorized = authorizedSiteIds.includes(site.id);
        return (
          <span style={{ color: isAuthorized ? 'green' : 'red', fontWeight: 'bold' }}>
            {site.nom}
          </span>
        );
      }
    }
    
    // PRIORITÉ 2 : Chercher le site correspondant à la position GPS
    const actualSite = findPositionSite(location);
    const authorizedSiteIds = employee && employee.siteDeTravail
      ? (Array.isArray(employee.siteDeTravail) ? employee.siteDeTravail : [employee.siteDeTravail])
      : [];
    
    if (actualSite) {
      const isAuthorized = authorizedSiteIds.includes(actualSite.id);
      return (
        <span style={{ color: isAuthorized ? 'green' : 'red', fontWeight: 'bold' }}>
          {actualSite.nom}
        </span>
      );
    } else {
      // HORS ZONE
      const lat = getLat(location);
      const lng = getLng(location);
      return (
        <div style={{ color: 'red', fontWeight: 'bold' }}>
          HORS ZONE
          {lat && lng && (
            <div>
              <a
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none', 
                  fontWeight: 'normal',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                📍Carte
              </a>
            </div>
          )}
        </div>
      );
    }
  };

  // Fonction utilitaire pour PDF/CSV : nom du site ou HORS ZONE
  function getZoneLabelSimple(location, sites) {
    if (!location) return "Non disponible";
    // Priorité au siteId si présent
    if (location.siteId) {
      const site = sites.find(s => s.id === location.siteId);
      return site ? site.nom : "HORS ZONE";
    }
    const actualSite = findPositionSite(location);
    return actualSite ? actualSite.nom : "HORS ZONE";
  }

  function getPauseZonesForExport(session, sites) {
    if (!session.breaks || !Array.isArray(session.breaks) || session.breaks.length === 0) return "";
    return session.breaks.map(breakItem => {
      // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
      const siteId = breakItem.siteId;
      if (siteId === "HORS_ZONE") return "HORS ZONE";
      
      // PRIORITÉ 2 : Utiliser le siteId des locations si disponible
      const startSiteId = breakItem.startLocation?.siteId;
      const endSiteId = breakItem.endLocation?.siteId;
      
      if (startSiteId === "HORS_ZONE" || endSiteId === "HORS_ZONE") return "HORS ZONE";
      
      // PRIORITÉ 3 : Chercher les sites par ID
      let startSite = startSiteId ? sites.find(s => s.id === startSiteId) : null;
      let endSite = endSiteId ? sites.find(s => s.id === endSiteId) : null;
      
      // PRIORITÉ 4 : Si pas trouvé par siteId, calculer à partir des coordonnées (comme dans getBreakLocations)
      if (!startSite && breakItem.startLocation?.latitude && breakItem.startLocation?.longitude) {
        startSite = findPositionSite(breakItem.startLocation);
      }
      if (!endSite && breakItem.endLocation?.latitude && breakItem.endLocation?.longitude) {
        endSite = findPositionSite(breakItem.endLocation);
      }
      
      const startSiteName = startSite ? startSite.nom : "HORS ZONE";
      const endSiteName = endSite ? endSite.nom : "HORS ZONE";
      
      // Si les sites sont différents, afficher les deux
      if (startSiteName !== endSiteName) {
        return `${startSiteName} → ${endSiteName}`;
      }
      
      return startSiteName;
    }).join(", ");
  }

  // Fonction pour ouvrir le modal de modification
  const handleEditSession = (session) => {
    const timestampToDateTimeLocal = (timestamp) => {
      
      if (!timestamp) {
        return '';
      }
      
      let date;
      
      if (timestamp.toDate) {
        // Timestamp Firestore avec méthode toDate()
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        // Timestamp avec seconds/nanoseconds
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'number') {
        // Timestamp numérique
        date = new Date(timestamp > 1e12 ? timestamp : timestamp * 1000);
      } else if (timestamp instanceof Date) {
        // Objet Date
        date = timestamp;
      } else {
        return '';
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const result = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      return result;
    };

    const getSiteFromLocation = (location) => {
      
      // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
      if (location && location.siteId) {
        return location.siteId;
      }
      
      if (!location || !location.latitude || !location.longitude) {
        return null;
      }
      
      // PRIORITÉ 2 : Cache pour éviter les appels redondants
      const locationKey = `${location.latitude},${location.longitude}`;
      if (siteCache[locationKey] !== undefined) {
        return siteCache[locationKey];
      }
      
      // PRIORITÉ 3 : Chercher le site à partir des coordonnées GPS
      const site = findPositionSite(location);
      siteCache[locationKey] = site ? site.id : null;
      
      if (site) {
      } else {
      }
      
      return site ? site.id : null;
    };

    // Pré-remplir les données du modal
    const prefillData = {
      startDate: session.startTime ? timestampToDateTimeLocal(session.startTime).split('T')[0] : '',
      startTime: session.startTime ? timestampToDateTimeLocal(session.startTime).split('T')[1] : '',
      startSite: session.startLocation?.siteId || (session.startLocation ? getSiteFromLocation(session.startLocation) : ''),
      endDate: session.endTime ? timestampToDateTimeLocal(session.endTime).split('T')[0] : '',
      endTime: session.endTime ? timestampToDateTimeLocal(session.endTime).split('T')[1] : '',
      endSite: session.endLocation?.siteId || (session.endLocation ? getSiteFromLocation(session.endLocation) : ''),
      breaks: session.breaks ? session.breaks.map(breakItem => {
        // Préserver "HORS_ZONE" si c'est déjà défini
        const startSiteId = breakItem.startLocation?.siteId || '';
        const endSiteId = breakItem.endLocation?.siteId || '';
        
        
        return {
          startTime: breakItem.startTime ? timestampToDateTimeLocal(breakItem.startTime) : '',
          endTime: breakItem.endTime ? timestampToDateTimeLocal(breakItem.endTime) : '',
          startLocation: {
            ...breakItem.startLocation,
            siteId: startSiteId || (breakItem.startLocation ? getSiteFromLocation(breakItem.startLocation) : '')
          },
          endLocation: {
            ...breakItem.endLocation,
            siteId: endSiteId || (breakItem.endLocation ? getSiteFromLocation(breakItem.endLocation) : '')
          }
        };
      }) : []
    };


    setEditSession(session);
    setEditData(prefillData);
    setShowEditModal(true);
  };

  // Fonction pour sauvegarder les modifications
  const handleSaveEdit = async () => {
    if (!editSession || isSaving) return; // Bloquer si déjà en cours de sauvegarde

    try {
      setIsSaving(true); // Bloquer le bouton
      
      // Convertir les données du modal en format Firestore
      const startDateTime = new Date(`${editData.startDate}T${editData.startTime}`);
      const endDateTime = new Date(`${editData.endDate}T${editData.endTime}`);
      
      // Mettre à jour la session avec les horaires réels modifiés
      const updatedSession = {
        ...editSession,
        startTime: startDateTime, // Horaires réels modifiés
        endTime: endDateTime,     // Horaires réels modifiés
        startLocation: {
          ...editSession.startLocation,
          siteId: editData.startSite
        },
        endLocation: {
          ...editSession.endLocation,
          siteId: editData.endSite
        },
        adminModified: true,
        adminModifiedAt: new Date(),
        adminModifiedBy: 'admin@example.com' // TODO: Récupérer l'email de l'admin connecté
      };

      // Supprimer les anciennes pauses
      if (editSession.breaks && editSession.breaks.length > 0) {
        for (const breakItem of editSession.breaks) {
          if (breakItem.id) {
            await deleteDoc(doc(db, 'breaks', breakItem.id));
          }
        }
      }

      // Créer les nouvelles pauses
      const newBreaks = [];
      let totalPauseTime = 0; // Calculer le temps total de pause
      
      if (editData.breaks && editData.breaks.length > 0) {
        for (const breakItem of editData.breaks) {
          if (breakItem.startTime && breakItem.endTime) {
            const breakStartTime = new Date(breakItem.startTime);
            const breakEndTime = new Date(breakItem.endTime);
            
            // Calculer la durée de cette pause
            const pauseDuration = breakEndTime.getTime() - breakStartTime.getTime();
            totalPauseTime += pauseDuration;
            
            // S'assurer que les siteId sont bien transmis
            const startLocation = {
              ...(breakItem.startLocation || {}),
              siteId: breakItem.startLocation?.siteId || null
            };
            
            const endLocation = {
              ...(breakItem.endLocation || {}),
              siteId: breakItem.endLocation?.siteId || null
            };
            
            const newBreak = {
              sessionId: editSession.id,
              startTime: breakStartTime,
              endTime: breakEndTime,
              startLocation: startLocation,
              endLocation: endLocation,
              createdAt: new Date()
            };
            
            const breakRef = await addDoc(collection(db, 'breaks'), newBreak);
            newBreaks.push({ ...newBreak, id: breakRef.id });
          }
        }
      }


      // Mettre à jour la session avec les nouvelles pauses et le temps total de pause
      updatedSession.breaks = newBreaks;
      updatedSession.totalPauseTime = totalPauseTime;

      // Sauvegarder dans Firestore
      await updateDoc(doc(db, 'workSessions', editSession.id), updatedSession);
      
      // IMPORTANT : Appeler applyToleranceRules pour recalculer automatiquement les horaires ajustés
      // Cela va recalculer adjustedStartTime, adjustedEndTime et adjustedWorkTime si des tolérances existent
      const { workSessionService } = await import('../services/workSessionService');
      await workSessionService.applyToleranceRules(editSession.id);
      
      // Attendre un peu avant de recharger pour s'assurer que tout est bien sauvegardé
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchWorkSessions();
      
      setShowEditModal(false);
      setEditSession(null);
      setEditData({});
      
      alert('Session modifiée avec succès !');
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde : ' + error.message);
    } finally {
      setIsSaving(false); // Débloquer le bouton
    }
  };

  // Correction : recalcul dynamique du pré-remplissage quand sites sont chargés et modal ouvert
  useEffect(() => {
    if (showEditModal && editSession && sites.length > 0) {
      
      // Éviter la duplication : ne recalculer que si les pauses n'ont pas déjà été traitées
      if (editData.breaks && editData.breaks.length > 0) {
        // Vérifier si les pauses ont déjà des siteId corrects
        const needsUpdate = editData.breaks.some(breakItem => 
          !breakItem.startLocation?.siteId || !breakItem.endLocation?.siteId
        );
        
        if (!needsUpdate) {
          return;
        }
      }
      
      // Recalculer les sites pour les pauses
      const updatedBreaks = editData.breaks ? editData.breaks.map(breakItem => {
        const startSiteId = breakItem.startLocation?.siteId || '';
        const endSiteId = breakItem.endLocation?.siteId || '';
        
        
        return {
          ...breakItem,
          startLocation: {
            ...breakItem.startLocation,
            siteId: startSiteId
          },
          endLocation: {
            ...breakItem.endLocation,
            siteId: endSiteId
          }
        };
      }) : [];
      
      
      setEditData({
        ...editData,
        breaks: updatedBreaks
      });
    }
  }, [sites, showEditModal, editSession]);

  // Charger la config admin (temps max)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configRef = doc(db, "adminConfigs", "global");
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
          const data = configSnap.data();
          if (data && data.maxSessionTimeHours) {
            setMaxSessionTime(Number(data.maxSessionTimeHours));
          }
        }
      } catch (e) {
        console.warn("Impossible de charger la config admin, utilisation de la valeur par défaut (12h)");
      }
    };
    fetchConfig();
  }, []);

  // Fonction utilitaire pour détecter une anomalie de temps
  const isSessionOverMax = (session) => {
    if (!session.startTime || !session.endTime) return false;
    const start = session.startTime.seconds ? session.startTime.seconds * 1000 : new Date(session.startTime).getTime();
    const end = session.endTime.seconds ? session.endTime.seconds * 1000 : new Date(session.endTime).getTime();
    const durationMs = end - start;
    return durationMs > maxSessionTime * 3600 * 1000;
  };

  if (loading) {
    return <div className="loading">Chargement en cours...</div>;
  }

  if (error) {
    return <div className="error">Erreur : {error}</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="logo-container">
        <img 
          src="/logo.png"
          alt="Logo" 
          style={{ 
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }} 
        />
      </div>
      <h1>Espace Administrateur</h1>
      

      <div className="admin-nav">
        <button className="admin-button" onClick={() => navigate("/admin/employees")}>Gestion des salariés</button>
        <button className="admin-button" onClick={() => navigate("/admin/sites")}>Gestion des zones de travail</button>
        <button className="admin-button" onClick={() => navigate("/admin/config")}>Configuration</button>
        <button 
          className="admin-button" 
          onClick={() => navigate("/admin/absences")}
          style={{position: 'relative'}}
        >
          Gestion des Absences
          {newAbsenceRequests > 0 && (
            <span style={{
              position: 'absolute', 
              top: 8, 
              right: 12, 
              minWidth: 16, 
              height: 16, 
              background: '#ff5722', 
              borderRadius: '50%', 
              border: '2px solid #fff', 
              boxShadow: '0 0 0 2px #ff5722', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#fff'
            }}>
              {newAbsenceRequests}
            </span>
          )}
        </button>
        <button className="admin-button" onClick={() => navigate("/admin/timesheet-validation")}>Validation Feuille de Temps</button>
        <button 
          className="admin-button" 
          onClick={() => navigate("/admin/escalations")}
          style={{position: 'relative'}}
        >
          Escalades
          {pendingEscalations > 0 && (
            <span style={{
              position: 'absolute', 
              top: 8, 
              right: 12, 
              minWidth: 16, 
              height: 16, 
              background: '#ff9800', 
              borderRadius: '50%', 
              border: '2px solid #fff', 
              boxShadow: '0 0 0 2px #ff9800', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#fff'
            }}>
              {pendingEscalations}
            </span>
          )}
        </button>
        <button className="admin-button" onClick={() => navigate("/admin/archives")}>Archives</button>
      </div>

      <div className="filters">
        <label>
          Filtrer par salarié :
          <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
            <option value="">Tous</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.email}>
                {emp.nom} {emp.prenom}
              </option>
            ))}
          </select>
        </label>

        <label>
          Mois :
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {months.map((month, idx) => {
              // Convertir YYYY-MM en format français lisible
              const [year, monthNum] = month.split('-');
              const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                                  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
              const monthLabel = `${monthNames[parseInt(monthNum) - 1]} ${year}`;
              
              return (
                <option key={idx} value={month}>{monthLabel}</option>
              );
            })}
          </select>
        </label>

        <div className="export-buttons">
          <button onClick={exportToCSV}>📥 Exporter en CSV</button>
          <button onClick={exportToPDF}>📄 Exporter en PDF</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', width: '100%', paddingBottom: showValidationBanner ? '120px' : '0' }}>
        <table className="session-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Nom</th>
              <th style={{ textAlign: 'center' }}>Date</th>
              <th style={{ textAlign: 'center' }}>Heure Début<br /><small>(Pondérée / Réelle)</small></th>
              <th style={{ textAlign: 'center' }}>Heure Fin<br /><small>(Pondérée / Réelle)</small></th>
              <th style={{ textAlign: 'center' }}>Temps Travail</th>
              <th style={{ textAlign: 'center' }}>Temps Pause</th>
              <th style={{ textAlign: 'center' }}>Sites autorisés</th>
              <th style={{ textAlign: 'center', width: '130px' }}>Zone Début</th>
              <th style={{ textAlign: 'center', width: '130px' }}>Zone Fin</th>
              <th style={{ textAlign: 'center', width: '150px' }}>Zone(s) Pause(s)</th>
              <th style={{ textAlign: 'center', width: '90px' }}>GPS Début</th>
              <th style={{ textAlign: 'center', width: '90px' }}>GPS Fin</th>
              <th style={{ textAlign: 'center', width: '80px' }}>📱 Appareil</th>
              <th style={{ width: 16, textAlign: 'center' }}>Info</th>
            </tr>
          </thead>
          <tbody>
            {pagedSessions.map((session) => (
              <tr key={session.id}>
                <td style={{ textAlign: 'center' }}>{getEmployeeDisplayName(session)}</td>
                <td style={{ textAlign: 'center' }}>{(() => {
                  const startTimestamp = session.startTime?.seconds;
                  if (!startTimestamp) return '';
                  const startDate = new Date(startTimestamp * 1000);
                  return startDate.toLocaleDateString('fr-FR');
                })()}</td>
                <td style={{ textAlign: 'center' }}>{getStartTimeDisplay(session)}</td>
                <td style={{ textAlign: 'center' }}>{getEndTimeDisplay(session)}</td>
                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{getWorkDuration(session)}</td>
                <td style={{ textAlign: 'center' }}>{getPauseDuration(session)}</td>
                <td style={{ textAlign: 'center' }}>{getEmployeeAuthorizedSites(session)}</td>
                <td style={{ textAlign: 'center' }}>{renderZoneCell(session.startLocation, employees.find(emp => emp.email === session.employeeEmail), sites, 'start')}</td>
                <td style={{ textAlign: 'center' }}>{renderZoneCell(session.endLocation, employees.find(emp => emp.email === session.employeeEmail), sites, 'end')}</td>
                <td style={{ textAlign: 'center' }}>{getBreakLocations(session)}</td>
                <td style={{ textAlign: 'center' }}>{(() => {
                  const gpsStart = session.startLocation;
                  return gpsStart ? getGPSLink(gpsStart) : <span className="no-location">Non disponible</span>;
                })()}</td>
                <td style={{ textAlign: 'center' }}>{(() => {
                  const gpsEnd = session.endLocation;
                  return gpsEnd ? getGPSLink(gpsEnd) : <span className="no-location">Non disponible</span>;
                })()}</td>
                <td style={{ 
                  textAlign: 'left', 
                  fontSize: '9px', 
                  maxWidth: '200px',
                  wordBreak: 'break-word',
                  padding: '4px 8px'
                }}>
                  {formatDeviceInfo(session.device)}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {isSessionOverMax(session) ? (
                    <span style={{ background: '#f44336', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Anomalie Temps de Session" onClick={() => handleEditSession(session)}></span>
                  ) : session.isEscalationValidated || session.originalEscalationId ? (
                    <span style={{ background: '#ff9800', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Session issue d'une escalade" onClick={() => handleEditSession(session)}></span>
                  ) : session.adminModified ? (
                    <span style={{ background: '#ffe066', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Session automatique modifiée manuellement" onClick={() => handleEditSession(session)}></span>
                  ) : (
                    <span style={{ background: '#2196f3', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Session automatique" onClick={() => handleEditSession(session)}></span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td colSpan="6" style={{ textAlign: "right" }}><strong>Total :</strong></td>
              <td><strong>{formatTime(pagedSessions.reduce((total, session) => {
                if (session.adjustedWorkTime != null) {
                  return total + session.adjustedWorkTime;
                }
                if (session.startTime?.seconds && session.endTime?.seconds) {
                  return total + Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
                }
                return total;
              }, 0))}</strong></td>
              <td><strong>{formatTime(pagedSessions.reduce((total, session) => total + (session.totalPauseTime || 0), 0))}</strong></td>
              <td colSpan="7">-</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Pagination style admin absences */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 16, gap: 16}}>
        <span>Éléments par page :</span>
        <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} style={{padding: '4px 8px', borderRadius: 6}}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span>{totalSessions === 0 ? '0' : `${page * rowsPerPage + 1} – ${Math.min((page + 1) * rowsPerPage, totalSessions)} sur ${totalSessions}`}</span>
        <button onClick={() => setPage(0)} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&#124;&lt;</button>
        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&lt;</button>
        <button onClick={() => setPage(p => Math.min(Math.ceil(totalSessions / rowsPerPage) - 1, p + 1))} disabled={page >= Math.ceil(totalSessions / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalSessions / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;</button>
        <button onClick={() => setPage(Math.max(0, Math.ceil(totalSessions / rowsPerPage) - 1))} disabled={page >= Math.ceil(totalSessions / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalSessions / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;&#124;</button>
      </div>

      {/* Modal d'édition */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Modifier la session</h3>
              <button onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            
            <div className="modal-body">
              {/* Section Début de session */}
              <div className="modal-section">
                <h4>🕐 Début de session</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Date de début :</label>
                    <input 
                      type="date" 
                      value={editData.startDate} 
                      onChange={e => setEditData({...editData, startDate: e.target.value})} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Heure de début :</label>
                    <input 
                      type="time" 
                      value={editData.startTime} 
                      onChange={e => setEditData({...editData, startTime: e.target.value})} 
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Site de début :</label>
                  <select 
                    value={String(editData.startSite || '')} 
                    onChange={e => setEditData({...editData, startSite: e.target.value})}
                  >
                    <option value="">Sélectionner un site</option>
                    <option value="HORS_ZONE">HORS ZONE</option>
                    {sites.map(site => (
                      <option key={site.id} value={String(site.id)}>{site.nom}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Section Fin de session */}
              <div className="modal-section">
                <h4>🕐 Fin de session</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Date de fin :</label>
                    <input 
                      type="date" 
                      value={editData.endDate} 
                      onChange={e => setEditData({...editData, endDate: e.target.value})} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Heure de fin :</label>
                    <input 
                      type="time" 
                      value={editData.endTime} 
                      onChange={e => setEditData({...editData, endTime: e.target.value})} 
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Site de fin :</label>
                  <select 
                    value={String(editData.endSite || '')} 
                    onChange={e => setEditData({...editData, endSite: e.target.value})}
                  >
                    <option value="">Sélectionner un site</option>
                    <option value="HORS_ZONE">HORS ZONE</option>
                    {sites.map(site => (
                      <option key={site.id} value={String(site.id)}>{site.nom}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Section Pauses */}
              <div className="modal-section">
                <h4>☕ Pauses</h4>
                
                {(editData.breaks && editData.breaks.length > 0 ? editData.breaks : []).map((b, idx, arr) => {
                  const startSiteId = b.startLocation?.siteId || '';
                  const endSiteId = b.endLocation?.siteId || '';
                  
                  return (
                    <div key={idx} className="break-item">
                      <div className="break-header">
                        <div className="break-title">
                          Pause n°{idx + 1}
                        </div>
                        {arr.length > 1 && (
                          <button 
                            className="break-delete-btn"
                            onClick={() => {
                              const newBreaks = editData.breaks.filter((_, i) => i !== idx);
                              setEditData({...editData, breaks: newBreaks});
                            }}
                            title="Supprimer cette pause"
                          >
                            🗑️ Supprimer
                          </button>
                        )}
                      </div>
                      
                      <div className="break-grid">
                        {/* Début de pause */}
                        <div className="break-section">
                          <label style={{ color: '#2196f3' }}>Début de pause :</label>
                          <input 
                            type="datetime-local" 
                            value={b.startTime} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].startTime = e.target.value;
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          />
                          <label style={{ marginTop: '8px' }}>Site début :</label>
                          <select 
                            value={startSiteId} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].startLocation = {
                                ...(newBreaks[idx].startLocation || {}), 
                                siteId: e.target.value
                              };
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          >
                            <option value="">Sélectionner un site</option>
                            <option value="HORS_ZONE">HORS ZONE</option>
                            {sites.map(site => (
                              <option key={site.id} value={String(site.id)}>{site.nom}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Fin de pause */}
                        <div className="break-section">
                          <label style={{ color: '#f44336' }}>Fin de pause :</label>
                          <input 
                            type="datetime-local" 
                            value={b.endTime} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].endTime = e.target.value;
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          />
                          <label style={{ marginTop: '8px' }}>Site fin :</label>
                          <select 
                            value={endSiteId} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].endLocation = {
                                ...(newBreaks[idx].endLocation || {}), 
                                siteId: e.target.value
                              };
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          >
                            <option value="">Sélectionner un site</option>
                            <option value="HORS_ZONE">HORS ZONE</option>
                            {sites.map(site => (
                              <option key={site.id} value={String(site.id)}>{site.nom}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                <button 
                  className="add-break-btn"
                  onClick={() => setEditData({
                    ...editData, 
                    breaks: [...(editData.breaks || []), {
                      startTime: '',
                      endTime: '',
                      startLocation: { siteId: '' },
                      endLocation: { siteId: '' }
                    }]
                  })}
                  title="Ajouter une nouvelle pause"
                >
                  ➕ Ajouter une pause
                </button>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-save" 
                onClick={handleSaveEdit}
                disabled={isSaving}
                style={{ 
                  background: isSaving ? '#ccc' : '#4caf50', 
                  color: 'white',
                  cursor: isSaving ? 'not-allowed' : 'pointer'
                }}
              >
                {isSaving ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </button>
              <button 
                className="btn-cancel" 
                onClick={() => setShowEditModal(false)}
                disabled={isSaving}
                style={{ 
                  background: isSaving ? '#ccc' : '#f44336', 
                  color: 'white',
                  cursor: isSaving ? 'not-allowed' : 'pointer'
                }}
              >
                ❌ Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bandeau de validation de feuille de temps */}
      {showValidationBanner && timesheetData && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '10px 20px',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
          minHeight: '56px'
        }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
              📋 {timesheetData.employeeName}
            </div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>
              {timesheetData.monthName} {timesheetData.year} • {timesheetData.sessionCount} sessions • {Math.round(timesheetData.totalWorkTime / 3600000)}h
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/admin/timesheet-validation')}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              ← Retour
            </button>
            
            <button
              onClick={() => setShowCommentModal(true)}
              style={{
                background: '#ffc107',
                color: '#000',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              📝 Notes
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('Refuser cette feuille de temps ?')) return;
                const reason = window.prompt('Raison du refus :');
                if (!reason) return;
                
                try {
                  await updateDoc(doc(db, 'workSessionsMonth', timesheetData.id), {
                    status: 'Refusé',
                    rejectionReason: reason,
                    adminValidatedAt: new Date(),
                    updatedAt: new Date()
                  });
                  alert('✅ Feuille refusée');
                  navigate('/admin/timesheet-validation');
                } catch (error) {
                  console.error('❌ Erreur:', error);
                  alert('Erreur lors du refus');
                }
              }}
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              ✗ Refuser
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('Valider cette feuille de temps ? L\'employé pourra la signer.')) return;
                
                try {
                  await updateDoc(doc(db, 'workSessionsMonth', timesheetData.id), {
                    status: 'En attente de signature salarié',
                    adminValidatedAt: new Date(),
                    updatedAt: new Date(),
                    employerComment: employerComment,
                    timesheetNotificationCreated: true,
                    timesheetNotificationVu: false
                  });
                  alert('✅ Feuille validée');
                  navigate('/admin/timesheet-validation');
                } catch (error) {
                  console.error('❌ Erreur:', error);
                  alert('Erreur lors de la validation');
                }
              }}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              ✓ Valider
            </button>
          </div>
        </div>
      )}

      {/* Modal de commentaires */}
      {showCommentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: 32,
            borderRadius: 12,
            maxWidth: 500,
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>💬 Notes et commentaires employeur</h3>
            <textarea
              value={employerComment}
              onChange={(e) => setEmployerComment(e.target.value)}
              placeholder="Ajoutez des notes ou commentaires concernant cette feuille de temps..."
              style={{
                width: '100%',
                minHeight: 120,
                padding: 12,
                borderRadius: 6,
                border: '1px solid #ddd',
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCommentModal(false)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  try {
                    await updateDoc(doc(db, 'workSessionsMonth', timesheetData.id), {
                      employerComment: employerComment,
                      updatedAt: new Date()
                    });
                    alert('✅ Commentaire sauvegardé');
                    setShowCommentModal(false);
                  } catch (error) {
                    console.error('❌ Erreur:', error);
                    alert('Erreur lors de la sauvegarde');
                  }
                }}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                💾 Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;